import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  clearAuthError,
  resendOtpAction,
  resetOtpFlow,
  selectIsAuthenticated,
  sendOtpLogin,
  verifyOtpLogin,
} from "../../redux/authSlice";
import Loader from "../common/Loader";
import "../common/Account.css";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, error, message, otpSent, mobileNumber } = useSelector(
    (s) => s.auth
  );
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const [form, setForm] = useState({
    mobileNumber: "",
    countryCode: "+91",
    otp: "",
  });
  const [fieldError, setFieldError] = useState("");

  useEffect(() => {
    dispatch(clearAuthError());
    return () => dispatch(resetOtpFlow());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(location.state?.from || "/", { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobileNumber") {
      setForm((prev) => ({
        ...prev,
        mobileNumber: value.replace(/\D/g, "").slice(0, 10),
      }));
      return;
    }
    if (name === "otp") {
      setForm((prev) => ({
        ...prev,
        otp: value.replace(/\D/g, "").slice(0, 6),
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setFieldError("");
    if (!/^\d{10}$/.test(form.mobileNumber)) {
      setFieldError("Enter a valid 10-digit mobile number");
      return;
    }
    await dispatch(
      sendOtpLogin({
        mobileNumber: form.mobileNumber,
        countryCode: form.countryCode,
      })
    );
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setFieldError("");
    if (!form.otp.trim()) {
      setFieldError("Enter the OTP sent to your phone");
      return;
    }
    const result = await dispatch(
      verifyOtpLogin({
        mobileNumber: form.mobileNumber || mobileNumber,
        otp: form.otp,
      })
    );
    if (verifyOtpLogin.fulfilled.match(result)) {
      navigate(location.state?.from || "/", { replace: true });
    }
  };

  const handleResend = () => {
    dispatch(
      resendOtpAction({
        mobileNumber: form.mobileNumber || mobileNumber,
        countryCode: form.countryCode || "+91",
      })
    );
  };

  const handleChangeNumber = () => {
    setForm((prev) => ({ ...prev, otp: "" }));
    setFieldError("");
    dispatch(resetOtpFlow());
  };

  return (
    <div className="account-page">
      {isLoading && <Loader overlay message="Please wait..." />}
      <div className="coustom_container">
        <Row className="justify-content-center">
          <Col lg={5} md={7} sm={10}>
            <div className="account-card">
              <h1 className="h3">Login</h1>
              <p className="text-muted mb-4">
                Enter your mobile number to receive an OTP and login.
              </p>

              {error && <Alert variant="danger">{error}</Alert>}
              {fieldError && <Alert variant="danger">{fieldError}</Alert>}
              {message && !error && !fieldError && (
                <Alert variant="success">{message}</Alert>
              )}

              {!otpSent ? (
                <Form onSubmit={handleSendOtp}>
                  <Form.Group className="mb-3">
                    <Form.Label>Mobile number</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>+91</InputGroup.Text>
                      <Form.Control
                        name="mobileNumber"
                        value={form.mobileNumber}
                        onChange={onChange}
                        placeholder="10-digit mobile number"
                        inputMode="numeric"
                        required
                        maxLength={10}
                        autoComplete="tel"
                      />
                    </InputGroup>
                  </Form.Group>

                  <Button type="submit" className="btn-kior w-100">
                    Send OTP
                  </Button>
                </Form>
              ) : (
                <Form onSubmit={handleVerifyOtp}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      OTP sent to +91 {form.mobileNumber || mobileNumber}
                    </Form.Label>
                    <Form.Control
                      name="otp"
                      value={form.otp}
                      onChange={onChange}
                      placeholder="Enter OTP"
                      inputMode="numeric"
                      required
                      maxLength={6}
                      autoComplete="one-time-code"
                    />
                  </Form.Group>

                  <Button type="submit" className="btn-kior w-100 mb-3">
                    Verify & Login
                  </Button>

                  <div className="d-flex justify-content-between flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="link"
                      className="p-0"
                      onClick={handleResend}
                    >
                      Resend OTP
                    </Button>
                    <Button
                      type="button"
                      variant="link"
                      className="p-0"
                      onClick={handleChangeNumber}
                    >
                      Change number
                    </Button>
                  </div>
                </Form>
              )}

              <p className="mt-4 mb-0 text-muted small">
                By continuing you agree to our{" "}
                <Link to="/terms-of-use">Terms</Link> and{" "}
                <Link to="/privacy-policy">Privacy Policy</Link>.
              </p>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Login;
